import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CheckIcon            from '@mui/icons-material/Check';
import CloseIcon            from '@mui/icons-material/Close';
import CloseOutlined        from '@mui/icons-material/CloseOutlined';
import DeleteForeverIcon    from '@mui/icons-material/DeleteForever';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon     from '@mui/icons-material/EditOutlined';
import SwapHorizIcon        from '@mui/icons-material/SwapHoriz';
import ContentDetailPanel        from './detail/ContentDetailPanel';
import ImagePreview             from './detail/ImagePreview';
import { buildComparisonRows, buildMembershipRows } from './detail/buildComparisonRows';
import BoothDeleteCascadeSection from './detail/BoothDeleteCascadeSection';
import TeleportMapComparisonRow from './detail/TeleportMapComparisonRow';
import RunDatesGrid from './detail/RunDatesGrid';
import {
  getItemTypes,
  getMerchandiseTypes,
  getMerchandiseCategories,
  getTicketTypes,
  getCurrencies,
  getTaxCategories,
} from '../../api/itemApi';
import { getPortalTypes } from '../../api/portalApi';
import { getAdPlacements } from '../../api/advertiserApi';
import { getContentEventTypes, getPlatformTypes, getEventFormats } from '../../api/eventApi';
import { CONTENT_TYPES } from '../../constants/contentTypes';

// ── Static lookup maps (never change between spaces) ─────────────────────────
const STATIC_LOOKUPS = {
  discountType: { 1: 'Percentage', 2: 'Flat' },
  mediaType:    { 1: 'Image', 2: 'Video', 3: 'Audio' },
};

// Converts an API array to { [id]: name } for fast resolution.
function toIdMap(arr, idKey, nameKey) {
  const map = {};
  if (!Array.isArray(arr)) return map;
  for (const item of arr) {
    const id = item[idKey];
    if (id != null) map[id] = item[nameKey] ?? String(id);
  }
  return map;
}

// ── Colour tokens ─────────────────────────────────────────────────────────────
const CHANGED_ROW_BG    = '#fffbeb';
const CURRENT_CELL_BG   = '#fff3e0';
const PROPOSED_CELL_BG  = '#e8f5e9';
const CURRENT_TEXT      = '#92400e';
const PROPOSED_TEXT     = '#15803d';
const UNCHANGED_TEXT    = '#374151';
const LABEL_COL_W       = '140px';

const ACTION_CONFIG = {
  create:  { label: 'New Content',      color: 'success', Icon: AddCircleOutlineIcon },
  edit:    { label: 'Edit',             color: 'warning', Icon: EditOutlinedIcon },
  delete:  { label: 'Delete',           color: 'error',   Icon: DeleteForeverIcon },
  convert: { label: 'Convert',          color: 'info',    Icon: SwapHorizIcon },
};

// ── Column header cell (used in the fixed header bar) ─────────────────────────
function ColHeaderCell({ label, subtitle, bgcolor, color }) {
  return (
    <Box sx={{ px: 2, py: 1.25, bgcolor, borderLeft: '1px solid #e2e8f0' }}>
      <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color, fontSize: 10, display: 'block' }}>
        {label}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 10, display: { xs: 'none', sm: 'block' } }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// ── Section header row (spans all 3 columns) ──────────────────────────────────
function SectionRow({ title }) {
  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        px: 2,
        py: 0.75,
        bgcolor: '#f1f5f9',
        borderTop: '1px solid #e2e8f0',
        borderBottom: '1px solid #e2e8f0',
        borderLeft: '3px solid #cbd5e1',
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
        {title}
      </Typography>
    </Box>
  );
}

// ── A sentence across the whole grid (impact of a membership change, a warning) ──
function NoteRow({ row }) {
  const warning = row.tone === 'warning';
  return (
    <Box sx={{ gridColumn: '1 / -1', px: 2, py: 1.25 }}>
      <Alert severity={warning ? 'warning' : 'info'} icon={false} sx={{ borderRadius: 2, py: 0.5 }}>
        <Typography variant="caption" fontWeight={600}>{row.text}</Typography>
      </Alert>
    </Box>
  );
}

// ── One comparison row — 3 cells: label | current | proposed ──────────────────
//
// Semantics:
//   isAdded   — locale/field exists only in proposed (current = null)  → green right only
//   isRemoved — locale/field exists only in current  (proposed = null) → amber left only
//   isChanged — both non-null but different                            → amber left + green right
function CompareRow({ row }) {
  const { label, currentValue, proposedValue, isChanged } = row;
  const isXs = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  // Run-date rows carry the date arrays and are drawn as an aligned grid; the dates an edit adds / removes
  // are highlighted so the approver sees exactly what changed.
  const hasDates    = Array.isArray(row.currentDates) || Array.isArray(row.proposedDates);
  const currentSet  = new Set(row.currentDates ?? []);
  const proposedSet = new Set(row.proposedDates ?? []);
  const removedDates = isChanged ? new Set([...currentSet].filter((d) => !proposedSet.has(d))) : undefined;
  const addedDates   = isChanged ? new Set([...proposedSet].filter((d) => !currentSet.has(d))) : undefined;
  const isAdded   = isChanged && currentValue   === null;
  const isRemoved = isChanged && proposedValue  === null;
  // Show amber highlight on current side only when there was a previous value
  const highlightCurrent  = isChanged && !isAdded;
  // Show green highlight on proposed side only when there is a new value
  const highlightProposed = isChanged && !isRemoved;

  // Phone: two ~100px value columns cannot hold a calendar each, so a run-date row spans the full width and
  // shows Current above Proposed, each with a full-width calendar.
  if (hasDates && isXs) {
    const captionSx = { display: 'block', fontWeight: 700, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', mb: 0.75 };
    return (
      <Box sx={{ gridColumn: '1 / -1', borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ px: 1, py: 0.75, bgcolor: isChanged ? CHANGED_ROW_BG : 'transparent' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', fontSize: 11 }}>{label}</Typography>
        </Box>
        <Box sx={{ px: 1, py: 1, bgcolor: highlightCurrent ? CURRENT_CELL_BG : 'white', borderTop: '1px solid #f1f5f9' }}>
          <Typography variant="caption" sx={{ ...captionSx, color: CURRENT_TEXT }}>Current (Live)</Typography>
          <RunDatesGrid dates={row.currentDates} marked={removedDates} markTone="removed" />
        </Box>
        <Box sx={{ px: 1, py: 1, bgcolor: highlightProposed ? PROPOSED_CELL_BG : 'white', borderTop: '1px solid #f1f5f9' }}>
          <Typography variant="caption" sx={{ ...captionSx, color: PROPOSED_TEXT }}>Proposed (After Approval)</Typography>
          <RunDatesGrid dates={row.proposedDates} marked={addedDates} markTone="added" />
        </Box>
      </Box>
    );
  }

  return (
    <>
      {/* Label cell */}
      <Box
        sx={{
          px: { xs: 1, sm: 2 },
          py: 0.75,
          bgcolor: isChanged ? CHANGED_ROW_BG : 'transparent',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        {/* A row can carry its own picture (a package member, a trial item) and a "type · price" line */}
        {row.thumb && (
          <Box
            component="img"
            src={row.thumb}
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            sx={{ width: 28, height: 28, borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0', mr: 1, flexShrink: 0 }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#6b7280', fontSize: 11, lineHeight: 1.4, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            {label}
          </Typography>
          {row.sub && (
            <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontSize: 10, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
              {row.sub}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Current value cell */}
      <Box
        sx={{
          px: { xs: 1, sm: 2 },
          py: 0.75,
          minWidth: 0,
          bgcolor: highlightCurrent ? CURRENT_CELL_BG : 'white',
          borderLeft: '1px solid #e2e8f0',
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        {hasDates ? (
          <RunDatesGrid dates={row.currentDates} marked={removedDates} markTone="removed" />
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: highlightCurrent ? CURRENT_TEXT : UNCHANGED_TEXT,
              textDecoration: highlightCurrent ? 'line-through' : 'none',
              opacity: highlightCurrent ? 0.65 : 1,
              overflowWrap: 'anywhere',
              wordBreak: 'normal',
              whiteSpace: 'pre-wrap',
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            {currentValue ?? '—'}
          </Typography>
        )}
        {row.preview && <ImagePreview url={currentValue} faded={highlightCurrent} />}
      </Box>

      {/* Proposed value cell */}
      <Box
        sx={{
          px: { xs: 1, sm: 2 },
          py: 0.75,
          minWidth: 0,
          bgcolor: highlightProposed ? PROPOSED_CELL_BG : 'white',
          borderLeft: '1px solid #e2e8f0',
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        {hasDates ? (
          <RunDatesGrid dates={row.proposedDates} marked={addedDates} markTone="added" />
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: highlightProposed ? PROPOSED_TEXT : UNCHANGED_TEXT,
              fontWeight: highlightProposed ? 600 : 400,
              overflowWrap: 'anywhere',
              wordBreak: 'normal',
              whiteSpace: 'pre-wrap',
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            {proposedValue ?? '—'}
          </Typography>
        )}
        {row.preview && <ImagePreview url={proposedValue} />}
      </Box>
    </>
  );
}

// ── Single-panel column layout (used for CREATE / DELETE) ─────────────────────
// Side by side from the md breakpoint up (each half scrolls on its own). Below that the halves stack and the
// whole layout scrolls, so a phone/tablet gives each panel the full width instead of a narrow half.
function TwoColLayout({ leftContent, rightContent }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%', overflowY: { xs: 'auto', md: 'visible' } }}>
      <Box
        sx={{
          flex: { xs: '0 0 auto', md: 1 },
          minWidth: 0,
          overflowY: { xs: 'visible', md: 'auto' },
          borderRight: { xs: 0, md: '1px solid #e2e8f0' },
          borderBottom: { xs: '1px solid #e2e8f0', md: 0 },
        }}
      >
        {leftContent}
      </Box>
      <Box sx={{ flex: { xs: '0 0 auto', md: 1 }, minWidth: 0, overflowY: { xs: 'visible', md: 'auto' } }}>
        {rightContent}
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
// Fallback only — prefer the sectionLabel prop (resolved server-side via
// ApprovalMatrix.GetSectionLabel) so multi-word snake_case types like
// "custom_booth_item" or "booth_media" render as "Custom Booth Item" /
// "Booth Media" instead of a naive first-letter capitalize.
const titleCaseContentType = (type) =>
  type
    ? type.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : type;

// Convert proposals' targetType uses a small 3-value vocabulary ("media"/"portal"/
// "custom_booth_item") distinct from ContentDetailPanel's PANELS keys, which use the full
// content_type string ("booth_media" for Media) — only "media" needs translating.
const TARGET_TYPE_TO_CONTENT_TYPE = {
  media: CONTENT_TYPES.BOOTH_MEDIA,
  portal: CONTENT_TYPES.PORTAL,
  custom_booth_item: CONTENT_TYPES.CUSTOM_BOOTH_ITEM,
};

export default function ContentComparisonView({
  open,
  onClose,
  contentType,
  sectionLabel,
  contentName,
  actionType,
  currentSnapshot,
  proposedSnapshot,
  proposedData,
  parentContext,
  onApprove,
  onReject,
  loading,
  readOnly = false,
  rejectionReason,
  isRejected = false,
  onEdit,
}) {
  const isCreate  = actionType === 'create';
  const isDelete  = actionType === 'delete';
  const isEdit    = actionType === 'edit';
  // "Convert" reuses the SOURCE's own content_type with a distinct action_type — it is NOT a
  // diff (buildComparisonRows assumes both sides share one shape); it's two independent panels
  // of two DIFFERENT content types: whatever's being deleted (left, = contentType prop, already
  // the source's own type) and whatever's being created (right, derived below from proposedData's
  // targetType). See docs\Booth_Item_Cross_Type_Conversion_Plan.md section 6.2.
  const isConvert = actionType === 'convert';

  const targetContentType = React.useMemo(() => {
    if (!isConvert || !proposedData) return null;
    try {
      const parsed = typeof proposedData === 'string' ? JSON.parse(proposedData) : proposedData;
      const targetType = parsed?.targetType;
      return TARGET_TYPE_TO_CONTENT_TYPE[targetType] ?? null;
    } catch {
      return null;
    }
  }, [isConvert, proposedData]);

  // ── Lookup maps: resolved FK ids → human-readable names ──────────────────
  const [lookups, setLookups] = React.useState(STATIC_LOOKUPS);

  React.useEffect(() => {
    if (!open) return;
    const type = contentType;

    if (type === CONTENT_TYPES.ITEM) {
      Promise.all([
        getItemTypes().catch(() => []),
        getMerchandiseTypes().catch(() => []),
        getMerchandiseCategories().catch(() => []),
        getTicketTypes().catch(() => []),
        getCurrencies().catch(() => []),
        getTaxCategories().catch(() => []),
      ]).then(([itemTypes, merchTypes, merchCats, ticketTypes, currencies, taxCategories]) => {
        setLookups((prev) => ({
          ...prev,
          itemType:        toIdMap(itemTypes,  'id', 'Type'),
          merchandiseType: toIdMap(merchTypes,  'id', 'Type'),
          merchandiseCat:  toIdMap(merchCats,   'id', 'Category'),
          ticketType:      toIdMap(ticketTypes, 'id', 'Type'),
          currency:        toIdMap(currencies,  'id', 'ISO'),
          taxCategory:     toIdMap(taxCategories, 'id', 'name'),
          taxCategories:   Array.isArray(taxCategories) ? taxCategories : [],
        }));
      });
    }


    if (type === CONTENT_TYPES.PACKAGE || type === CONTENT_TYPES.PACKAGE_ITEM || type === CONTENT_TYPES.FREE_TRIAL_ITEM) {
      Promise.all([
        getCurrencies().catch(() => []),
        getTaxCategories().catch(() => []),
      ]).then(([currencies, taxCategories]) => {
        setLookups((prev) => ({
          ...prev,
          currencyList:  Array.isArray(currencies) ? currencies : [],
          taxCategories: Array.isArray(taxCategories) ? taxCategories : [],
        }));
      });
    }

    // A free-trial entry names its item's type.
    if (type === CONTENT_TYPES.FREE_TRIAL_ITEM) {
      getItemTypes().catch(() => []).then((itemTypes) => {
        setLookups((prev) => ({ ...prev, itemType: toIdMap(itemTypes, 'id', 'Type') }));
      });
    }

    // An ad's placement is shown by its key, resolved from the placement id (see buildAdRows).
    if (type === CONTENT_TYPES.AD) {
      getAdPlacements().catch(() => []).then((placements) => {
        setLookups((prev) => ({ ...prev, adPlacement: toIdMap(placements, 'id', 'placementKey') }));
      });
    }

    // a 3D ad carries a portal too (shown with its type, see buildAdRows)
    if (type === CONTENT_TYPES.PORTAL || type === CONTENT_TYPES.BOOTH || type === CONTENT_TYPES.AD) {
      getPortalTypes().catch(() => []).then((types) => {
        setLookups((prev) => ({
          ...prev,
          portalType: toIdMap(types, 'id', 'name'),
        }));
      });
    }

    if (type === CONTENT_TYPES.EVENT) {
      Promise.all([
        getContentEventTypes().catch(() => []),
        getPlatformTypes().catch(() => []),
        getEventFormats().catch(() => []),
      ]).then(([eventTypes, platformTypes, eventFormats]) => {
        setLookups((prev) => ({
          ...prev,
          eventType:    toIdMap(eventTypes,    'id', 'Type'),
          platformType: toIdMap(platformTypes, 'id', 'PlatformName'),
          eventFormat:  toIdMap(eventFormats,  'id', 'Name'),
        }));
      });
    }
  }, [open, contentType]);


  const isMembership = contentType === CONTENT_TYPES.PACKAGE_ITEM || contentType === CONTENT_TYPES.FREE_TRIAL_ITEM;
  const useGrid      = isEdit || (isMembership && (isCreate || isDelete));

  const rows = React.useMemo(() => {
    if (isEdit) return buildComparisonRows(contentType, currentSnapshot, proposedSnapshot, lookups);
    if (isMembership && (isCreate || isDelete)) {
      return buildMembershipRows(contentType, actionType, isCreate ? proposedSnapshot : currentSnapshot, lookups);
    }
    return [];
  }, [contentType, currentSnapshot, proposedSnapshot, isEdit, isCreate, isDelete, isMembership, actionType, lookups]);

  const changeCount = rows.filter((r) => r.type === 'field' && r.isChanged).length;
  const action      = ACTION_CONFIG[actionType] ?? ACTION_CONFIG.edit;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 8px 24px rgba(10,50,109,0.12)',
          maxHeight: { xs: '95vh', sm: '92vh' },
          height: { xs: '95vh', sm: '92vh' },
          display: 'flex',
          flexDirection: 'column',
          m: { xs: 0.5, sm: 2 },
        },
      }}
    >
      {/* ── Title ──────────────────────────────────────────────────────── */}
      <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" fontWeight={700} sx={{ color: '#1e3a5f' }}>
              {isCreate ? 'New Content' : isDelete ? 'Deletion Review' : isConvert ? 'Convert to Another Type' : 'Compare Changes'}
            </Typography>
            {contentType && (
              <Chip
                label={sectionLabel || titleCaseContentType(contentType)}
                size="small"
                sx={{ bgcolor: '#eff6ff', color: '#1e40af', fontWeight: 700, fontSize: 11 }}
              />
            )}
            <Chip
              icon={<action.Icon sx={{ fontSize: 13 }} />}
              label={action.label}
              size="small"
              color={action.color}
              variant="outlined"
              sx={{ fontWeight: 700, fontSize: 11 }}
            />
            {isEdit && changeCount > 0 && (
              <Chip
                label={`${changeCount} field${changeCount > 1 ? 's' : ''} changed`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 700, fontSize: 10, height: 20 }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.25 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {contentName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {contentName}
          </Typography>
        )}
        {parentContext && (
          <Alert severity="info" icon={false} sx={{ mt: 1, py: 0.5, borderRadius: 1.5 }}>
            <Typography variant="caption" fontWeight={600}>
              Parent {parentContext.type}: <strong>{parentContext.name}</strong>
            </Typography>
          </Alert>
        )}
        {readOnly && rejectionReason && (
          <Alert severity="error" sx={{ mt: 1, py: 0.5, borderRadius: 1.5 }}>
            <Typography variant="caption" fontWeight={600}>
              Rejected: {rejectionReason}
            </Typography>
          </Alert>
        )}
      </DialogTitle>

      <Divider />

      {/* ── Fixed column headers (EDIT, and a package member / trial entry add or remove) ── */}
      {useGrid && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '80px 1fr 1fr', sm: `${LABEL_COL_W} 1fr 1fr` },
            borderBottom: '1px solid #e2e8f0',
            flexShrink: 0,
          }}
        >
          <Box sx={{ px: 2, py: 1, bgcolor: '#f8fafc' }} />
          <ColHeaderCell
            label="Current (Live)"
            subtitle="What is live right now"
            bgcolor="#fff7ed"
            color="#92400e"
          />
          <ColHeaderCell
            label="Proposed (After Approval)"
            subtitle="What this will become if approved"
            bgcolor="#f0fdf4"
            color="#15803d"
          />
        </Box>
      )}

      {/* ── Scrollable content ──────────────────────────────────────────── */}
      <DialogContent sx={{ p: 0, flexGrow: 1, overflow: 'hidden' }}>

        {/* ── EDIT: unified 3-column row grid ──────────────────────────── */}
        {useGrid && (
          <Box sx={{ height: '100%', overflowY: 'auto' }}>
            {!currentSnapshot && !proposedSnapshot ? (
              <Box sx={{ px: 2.5, py: 3 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} mb={0.5}>
                    Snapshot data unavailable
                  </Typography>
                  <Typography variant="body2">
                    This proposal was submitted before comparison snapshots were supported.
                    Withdraw and resubmit the proposal to enable side-by-side comparison.
                  </Typography>
                </Alert>
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ px: 2.5, py: 2.5 }}>
                <Typography variant="body2" color="text.secondary">
                  No field data available for comparison.
                </Typography>
              </Box>
            ) : (
              <Box>
                {isEdit && !currentSnapshot && (
                  <Alert severity="warning" icon={false} sx={{ borderRadius: 0, py: 0.75 }}>
                    <Typography variant="caption">
                      Current (live) snapshot unavailable — current values show as "—". Withdraw and resubmit to capture the live state.
                    </Typography>
                  </Alert>
                )}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '80px 1fr 1fr', sm: `${LABEL_COL_W} 1fr 1fr` },
                  }}
                >
                  {rows.map((row, i) =>
                    row.type === 'section'
                      ? <SectionRow key={i} title={row.title} />
                      : row.type === 'mapVisual'
                        ? <TeleportMapComparisonRow key={i} currentSpotId={row.currentSpotId} proposedSpotId={row.proposedSpotId} />
                        : row.type === 'note'
                          ? <NoteRow key={i} row={row} />
                          : <CompareRow key={i} row={row} />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* ── CREATE: left placeholder + right full detail ──────────────── */}
        {isCreate && !isMembership && (
          <TwoColLayout
            leftContent={
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <AddCircleOutlineIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
                <Typography variant="body2">
                  This is a new content item. There is no current version.
                </Typography>
              </Box>
            }
            rightContent={
              <ContentDetailPanel
                contentType={contentType}
                data={proposedSnapshot}
                diffKeys={new Set()}
                side="proposed"
                lookups={lookups}
              />
            }
          />
        )}

        {/* ── DELETE (non-booth): left detail + right permanent-deletion warning ── */}
        {isDelete && contentType !== CONTENT_TYPES.BOOTH && !isMembership && (
          <TwoColLayout
            leftContent={
              <ContentDetailPanel
                contentType={contentType}
                data={currentSnapshot}
                diffKeys={new Set()}
                side="current"
                lookups={lookups}
              />
            }
            rightContent={
              <Box sx={{ p: 3 }}>
                {contentType === CONTENT_TYPES.PACKAGE ? (
                  <Alert severity="error" icon={<DeleteForeverIcon />} sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight={700} mb={0.5}>
                      Package Deletion
                    </Typography>
                    <Typography variant="caption" display="block">
                      Approving will delete <strong>{contentName || 'this package'}</strong>.
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      It also removes the package's item memberships, its free-trial listing (if it has one) and any
                      free-trial grants players hold through it.
                    </Typography>
                  </Alert>
                ) : contentType === CONTENT_TYPES.GAME_SCHEDULE ? (
                  <Alert severity="error" icon={<DeleteForeverIcon />} sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight={700} mb={0.5}>
                      Quiz Deletion
                    </Typography>
                    <Typography variant="caption" display="block">
                      Approving will delete <strong>{contentName || 'this quiz'}</strong> and take it off the schedule.
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      If the quiz has already started, the points fans earned from it are taken back from their totals
                      (all-time, weekly and monthly), and fans left with no plays leave the quiz leaderboard.
                    </Typography>
                    {currentSnapshot?.linkedAnnouncement && (
                      (currentSnapshot.linkedAnnouncement.quizCount ?? 1) > 1 ? (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          It is removed from its announcement <strong>{currentSnapshot.linkedAnnouncement.name}</strong>, which
                          keeps {currentSnapshot.linkedAnnouncement.quizCount - 1 === 1
                            ? 'its other quiz'
                            : `its other ${currentSnapshot.linkedAnnouncement.quizCount - 1} quizzes`}.
                        </Typography>
                      ) : (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Its linked announcement <strong>{currentSnapshot.linkedAnnouncement.name}</strong> is deleted with it.
                        </Typography>
                      )
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error" icon={<DeleteForeverIcon />} sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight={700} mb={0.5}>
                      Permanent Deletion
                    </Typography>
                    <Typography variant="caption">
                      Approving will permanently delete{' '}
                      <strong>{contentName || 'this content'}</strong>.
                      This cannot be undone.
                    </Typography>
                  </Alert>
                )}
              </Box>
            }
          />
        )}

        {/* ── CONVERT: left = source being deleted, right = target being created ── */}
        {/* Not a diff — two independent panels of two different content types, side by side.
            Left is always the source's own type (contentType prop); right is derived from
            proposedData's targetType — works for all 6 Portal/Media/Custom pairs, not just
            Portal→Media. */}
        {isConvert && (
          <TwoColLayout
            leftContent={
              <Box>
                <Alert severity="warning" icon={<DeleteForeverIcon />} sx={{ borderRadius: 0 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography variant="body2" fontWeight={700}>
                      Will be deleted
                    </Typography>
                    <Chip
                      label={sectionLabel || titleCaseContentType(contentType)}
                      size="small"
                      sx={{ bgcolor: 'white', color: '#92400e', fontWeight: 700, fontSize: 11, border: '1px solid #fde68a' }}
                    />
                  </Box>
                </Alert>
                <ContentDetailPanel
                  contentType={contentType}
                  data={currentSnapshot}
                  diffKeys={new Set()}
                  side="current"
                />
              </Box>
            }
            rightContent={
              <Box>
                <Alert severity="success" icon={<AddCircleOutlineIcon />} sx={{ borderRadius: 0 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography variant="body2" fontWeight={700}>
                      Will be created (as Draft)
                    </Typography>
                    {targetContentType && (
                      <Chip
                        label={titleCaseContentType(targetContentType)}
                        size="small"
                        sx={{ bgcolor: 'white', color: '#15803d', fontWeight: 700, fontSize: 11, border: '1px solid #bbf7d0' }}
                      />
                    )}
                  </Box>
                </Alert>
                {targetContentType ? (
                  <ContentDetailPanel
                    contentType={targetContentType}
                    data={proposedSnapshot}
                    diffKeys={new Set()}
                    side="proposed"
                  />
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Target type unavailable for preview.
                    </Typography>
                  </Box>
                )}
              </Box>
            }
          />
        )}

        {/* ── DELETE booth: main row + full-width cascade accordions ────────── */}
        {isDelete && contentType === CONTENT_TYPES.BOOTH && (
          <Box sx={{ height: '100%', overflowY: 'auto' }}>
            {/* Booth identity (left) | permanent-deletion warning (right) */}
            <Box sx={{ display: 'flex', borderBottom: '2px solid #fee2e2' }}>
              <Box sx={{ flex: 1, borderRight: '1px solid #e2e8f0' }}>
                <ContentDetailPanel
                  contentType={contentType}
                  data={currentSnapshot}
                  diffKeys={new Set()}
                  side="current"
                />
              </Box>
              <Box sx={{ flex: 1, p: 3 }}>
                <Alert severity="error" icon={<DeleteForeverIcon />} sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} mb={0.5}>
                    Permanent Deletion
                  </Typography>
                  <Typography variant="caption">
                    Approving will permanently delete{' '}
                    <strong>{contentName || 'this content'}</strong>.
                    This cannot be undone.
                  </Typography>
                </Alert>
              </Box>
            </Box>

            {/* Cascade: each portal/media as a full-width collapsible with left/right detail */}
            <Box sx={{ px: 2.5, py: 2.5 }}>
              <BoothDeleteCascadeSection
                boothItems={proposedSnapshot?.boothItems}
                boothId={currentSnapshot?.id}
                portalTypeMap={lookups.portalType}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, flexShrink: 0, borderTop: '1px solid #f1f5f9' }}>
        <Button
          size="small"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: '#475569',
            borderRadius: '8px',
            px: 1.5,
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          Close
        </Button>
        {readOnly && onEdit && isRejected && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              onClick={() => onEdit(proposedSnapshot)}
              sx={{
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: '8px',
                border: '1px solid #bfdbfe',
                color: '#1d4ed8',
                bgcolor: '#eff6ff',
                px: 1.5,
                '&:hover': { bgcolor: '#dbeafe', borderColor: '#93c5fd' },
              }}
            >
              Edit &amp; Resubmit
            </Button>
          </>
        )}
        {!readOnly && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              startIcon={<CloseOutlined sx={{ fontSize: 14 }} />}
              onClick={onReject}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: '8px',
                border: '1px solid #fecaca',
                color: '#dc2626',
                bgcolor: '#fef2f2',
                px: 1.5,
                '&:hover': { bgcolor: '#fee2e2', borderColor: '#fca5a5' },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              Reject
            </Button>
            <Button
              size="small"
              startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
              onClick={onApprove}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 700,
                borderRadius: '8px',
                border: '1px solid transparent',
                color: 'white',
                bgcolor: '#16a34a',
                px: 2,
                minWidth: 110,
                boxShadow: '0 1px 4px rgba(22,163,74,0.3)',
                '&:hover': { bgcolor: '#15803d' },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              {loading ? 'Approving…' : 'Approve'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
